import { and, asc, eq, isNotNull, sql } from "drizzle-orm";

import type { WorkspaceMembership as WsWorkspaceMembership } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { db } from "@/db/db";
import { account, directWsInvitation, permission, workspaceMembership } from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";

export async function bulkUpsertWorkspaceMembership(records: WsWorkspaceMembership[]) {
  if (records.length === 0) return;
  const deduped = deduplicateByKeyValue(records, (item) => item.id);
  await bulkInsert(workspaceMembership, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [workspaceMembership.id],
      set: {
        accountId: sql`"excluded"."accountId"`,
        status: sql`"excluded"."status"`,
        role: sql`"excluded"."role"`,
      },
    },
  });
  emitTableUpdates(["workspace_membership"]);
}

export async function listFeedPermissions(workspaceMembershipId: string, feedId: string) {
  return await db.query.permission
    .findMany({
      where: and(eq(permission.feedId, feedId), eq(permission.workspaceMembershipId, workspaceMembershipId)),
    })
    .execute();
}

export function workspaceMembershipsQuery({ workspaceId }: { workspaceId?: string }) {
  const query = db
    .select({
      accountId: workspaceMembership.accountId,
      role: workspaceMembership.role,
      membershipId: sql`${workspaceMembership.id} as membershipId`,
      email: sql`COALESCE(${account.email}, ${directWsInvitation.email}) as email`,
      phoneNumber: sql`COALESCE(${account.phoneNumber}, ${directWsInvitation.phoneNumber}) as "phoneNumber"`,
      name: sql`COALESCE(${account.name}, ${directWsInvitation.name}) as name`,
      avatarColor: account.avatarColor,
    })
    .from(workspaceMembership)
    .leftJoin(account, eq(workspaceMembership.accountId, account.id))
    .leftJoin(directWsInvitation, eq(workspaceMembership.id, directWsInvitation.workspaceMembershipId))
    .where(and(eq(workspaceMembership.workspaceId, workspaceId), eq(workspaceMembership.status, "active")));
  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Workspace Memberships", query);
  return query;
}

export function workspaceMemberCountResponseSelect({ workspaceId }: { workspaceId?: string }) {
  const query = db
    .select({
      memberCount: sql`COUNT(${workspaceMembership.accountId}) as "memberCount"`,
    })
    .from(workspaceMembership)
    .where(
      and(
        eq(workspaceMembership.workspaceId, workspaceId),
        eq(workspaceMembership.status, "active"),
        isNotNull(workspaceMembership.accountId),
      ),
    );

  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Workspace Member Count", query);
  return query;
}

export function myWorkspaceMembershipsQuery({
  accountId,
  currentWorkspaceId,
}: {
  accountId?: string;
  currentWorkspaceId?: string;
}) {
  const query = db
    .select()
    .from(workspaceMembership)
    .where(
      and(
        eq(workspaceMembership.workspaceId, currentWorkspaceId),
        eq(workspaceMembership.accountId, accountId),
        eq(workspaceMembership.status, "active"),
      ),
    );

  if (!accountId || !currentWorkspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("My Workspace Memberships", query);
  return query;
}

export function justWorkspaceMembershipsQuery({ workspaceId }: { workspaceId?: string }) {
  const query = db
    .select()
    .from(workspaceMembership)
    .where(and(eq(workspaceMembership.workspaceId, workspaceId), eq(workspaceMembership.status, "active")));
  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Just Workspace Memberships", query);
  return query;
}

export function workspaceMembersTableSelect({ workspaceId }: { workspaceId?: string }) {
  const query = db
    .select({
      id: account.id,
      email: account.email,
      phoneNumber: account.phoneNumber,
      name: account.name,
      mine: account.mine,

      role: workspaceMembership.role,
      membershipId: sql`${workspaceMembership.id} as "membershipId"`,
    })
    .from(workspaceMembership)
    .innerJoin(account, eq(workspaceMembership.accountId, account.id))
    .where(and(eq(workspaceMembership.workspaceId, workspaceId), eq(workspaceMembership.status, "active")))
    .orderBy(asc(account.name));

  if (!workspaceId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Workspace Members", query);
  return query;
}
